import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Bell, Car, Plus, Trash2, Radio, Check, X, Download } from 'lucide-react';
import { vehicleAPI } from '../../../services/api';
import { toast } from 'sonner';

export function NoticesCard({ notices, onDelete, onCreate }) {
  return (
    <Card className="shadow-lg border-0">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5" /> Notices Management</CardTitle>
        <Button data-testid="create-notice-button" onClick={onCreate} size="sm" className="bg-gk-teal hover:bg-gk-teal-hover">
          <Plus className="w-4 h-4 mr-1" /> Post Notice
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {notices.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No notices posted</p>
          ) : notices.map((notice) => (
            <div key={notice.id} data-testid={`notice-item-${notice.id}`} className="p-4 bg-slate-50 rounded-lg border-l-4 border-gk-teal flex justify-between items-start">
              <div className="flex-1">
                <h4 className="font-semibold mb-1">{notice.title}</h4>
                <p className="text-sm text-slate-600 mb-2">{notice.content}</p>
                <div className="flex items-center gap-3">
                  <p className="text-xs text-slate-500">By {notice.author_name}</p>
                  {notice.expires_at && <p className="text-xs text-amber-600 font-medium">Expires: {notice.expires_at}</p>}
                </div>
              </div>
              <Button data-testid={`delete-notice-${notice.id}`} onClick={() => onDelete(notice.id)} size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function VehiclesCard({ vehicles, onDelete, onCreate, onChange }) {
  return (
    <Card className="shadow-lg border-0">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Car className="w-5 h-5" /> Vehicle Management</CardTitle>
        <div className="flex items-center gap-2">
          <Button
            data-testid="export-vehicles-csv-button"
            onClick={async () => {
              try {
                const res = await vehicleAPI.exportCsv();
                const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                const a = document.createElement('a');
                a.href = url;
                const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                a.download = `vehicles_${today}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success('Vehicles CSV downloaded');
              } catch (err) {
                toast.error('Failed to download CSV');
              }
            }}
            size="sm"
            variant="outline"
            className="text-slate-700"
          >
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
          <Button data-testid="add-vehicle-button" onClick={onCreate} size="sm" className="bg-gk-teal hover:bg-gk-teal-hover">
            <Plus className="w-4 h-4 mr-1" /> Add Vehicle
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {vehicles.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No vehicles registered</p>
          ) : vehicles.map((vehicle) => (
            <div key={vehicle.id} data-testid={`vehicle-item-${vehicle.id}`} className="p-4 bg-slate-50 rounded-lg flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-mono font-bold text-lg">{vehicle.plate_number}</p>
                <p className="text-sm text-slate-600">{vehicle.owner_name}</p>
                <p className="text-xs text-slate-500">Villa {vehicle.villa_number}</p>
                <ManagementRFIDInline vehicle={vehicle} onChange={onChange} />
              </div>
              <Button data-testid={`delete-vehicle-${vehicle.id}`} onClick={() => onDelete(vehicle.id)} size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ManagementRFIDInline({ vehicle, onChange }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(vehicle.epc_hex || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(vehicle.epc_hex || '');
  }, [vehicle.epc_hex]);

  const save = async () => {
    const cleaned = value.trim().toUpperCase();
    if (cleaned && !/^[0-9A-F]{1,64}$/.test(cleaned)) {
      toast.error('EPC must be hex characters (0-9, A-F)');
      return;
    }
    setSaving(true);
    try {
      await vehicleAPI.setEPC(vehicle.id, cleaned);
      toast.success(cleaned ? 'RFID tag updated' : 'RFID tag cleared');
      setEditing(false);
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update RFID tag');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div
        className="mt-1 flex items-center gap-1.5 text-[11px]"
        data-testid={`mgmt-vehicle-rfid-${vehicle.id}`}
      >
        <Radio className="w-3 h-3 text-slate-400" />
        <span className="text-slate-500">RFID:</span>
        {vehicle.epc_hex ? (
          <span
            className="font-mono font-semibold text-slate-700 tracking-wide"
            data-testid={`mgmt-vehicle-rfid-value-${vehicle.id}`}
          >
            {vehicle.epc_hex}
          </span>
        ) : (
          <span className="italic text-slate-400">no tag</span>
        )}
        <button
          onClick={() => setEditing(true)}
          className="ml-1 text-[10px] font-medium text-gk-teal hover:underline"
          data-testid={`mgmt-vehicle-rfid-edit-${vehicle.id}`}
        >
          {vehicle.epc_hex ? 'change' : 'add tag'}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1 flex items-center gap-1.5 text-[11px]">
      <Radio className="w-3 h-3 text-slate-400" />
      <Input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') setEditing(false);
        }}
        placeholder="EPC hex"
        maxLength={64}
        className="h-6 font-mono uppercase tracking-wider text-[11px] px-2 py-0 w-44"
        data-testid={`mgmt-vehicle-rfid-input-${vehicle.id}`}
      />
      <button
        onClick={save}
        disabled={saving}
        className="text-emerald-600 hover:text-emerald-800 disabled:opacity-50"
        data-testid={`mgmt-vehicle-rfid-save-${vehicle.id}`}
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => { setValue(vehicle.epc_hex || ''); setEditing(false); }}
        className="text-slate-400 hover:text-slate-600"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
